import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6f9fb98f"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "close-position__content" }
const _hoisted_2 = { class: "close-position__info" }
const _hoisted_3 = { class: "position-header" }
const _hoisted_4 = { class: "position-header__left" }
const _hoisted_5 = { class: "position-header__details" }
const _hoisted_6 = { class: "position-header__title" }
const _hoisted_7 = { class: "position-header__symbol" }
const _hoisted_8 = { class: "position-header__size" }
const _hoisted_9 = { class: "close-position__form" }
const _hoisted_10 = { class: "order-type-switcher" }
const _hoisted_11 = {
  key: 0,
  class: "close-position-form__input-wrapper"
}
const _hoisted_12 = { class: "close-position-form__input-wrapper" }

import { useVuelidate } from '@vuelidate/core';
import { maxValue, minValue, required } from '@vuelidate/validators';
import { ref, computed, type PropType, reactive, onMounted, watch } from 'vue';

import { trimNumberToPrecision } from '@exchange/helpers/number-transform-helper';
import type { PerpsPosition } from '@exchange/libs/account/service/src/lib/wss-account-messages';
import { marketService } from '@exchange/libs/market/service/src/index';
import { modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import OrderSizeInput from '@exchange/libs/order/order-form/ui/src/lib/components/OrderSizeInput.vue';
import useOrderTypes from '@exchange/libs/order/order-form/ui/src/lib/OrderTypes';
import useOrderFormPrice from '@exchange/libs/order/order-form/ui/src/lib/useOrderFormPrice';
import { OrderSide } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClosePositionForm',
  props: {
  position: { type: Object as PropType<PerpsPosition>, required: true },
  variant: { type: String as PropType<ModalVariant>, default: modalVariant.dark },
},
  emits: ["close", "submit"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { getMarket } = marketService;

const { market: marketType, limit: limitType } = useOrderTypes();
const activeType = ref(marketType.type);

const market = computed(() => getMarket(props.position.instrument_code));

const { marketOrderPrice } = useOrderFormPrice(
  market,
  computed(() => activeType.value === marketType.type),
);

const positionSize = computed(() => `${props.position.open_volume} ${market.value?.base?.code}`);

const limitPriceValue = ref<string | null>(null);
const amountValue = ref<string | null>(null);
const totalValue = ref<string | null>(null);
const sliderPercentage = ref(100); // Default to 100%

// Validation rules
const validationRules = computed(() => ({
  amountValue: {
    required,
    minAmount: minValue(0),
    maxAmount: maxValue(props.position.open_volume),
    $autoDirty: true,
  },
  limitPriceValue:
    activeType.value === limitType.type
      ? {
          required,
          minPrice: minValue(0),
          $autoDirty: true,
        }
      : {},
}));

const validationState = reactive({
  amountValue,
  limitPriceValue,
});

const v = useVuelidate(validationRules, validationState);

// Input handlers
const onAmountInput = (value: string) => {
  amountValue.value = value;
};

const onLimitPriceInput = (value: string) => {
  limitPriceValue.value = value;
};

const onTotalInput = (value: string) => {
  totalValue.value = value;
};

const marketPrice = computed(() => '1');

// Set initial values immediately when component mounts
onMounted(() => {
  // Set initial amount value
  amountValue.value = props.position.open_volume.toString();

  // If it's a limit order, maybe set an initial price
  if (activeType.value === limitType.type) {
    limitPriceValue.value = marketPrice.value;
  }

  // Calculate total value based on amount and price
  if (amountValue.value && marketPrice.value) {
    totalValue.value = (Number(amountValue.value) * Number(marketPrice.value)).toString();
  }
});

// Determine order side based on position direction
const orderSide = computed(() => (props.position.direction === 'LONG' ? OrderSide.SELL : OrderSide.BUY));

const isSubmitting = ref(false);

const onClosePosition = async () => {
  if (v.value.$invalid) {
    v.value.$touch();
    return;
  }

  try {
    // Set up order form service with our values
    orderformService.setSide(orderSide.value);
    orderformService.setType(activeType.value);

    isSubmitting.value = true;

    const payload = await orderformService.getPostOrderPayload({
      instrument: props.position.instrument_code,
      amount: amountValue.value,
      limitPrice: activeType.value === limitType.type ? limitPriceValue.value : null,
      stopPrice: null,
      minPrice: '0',
      maxPrice: '999999999',
    });

    // Submit the order
    const response = await orderformService.postOrder.rest(payload);

    if (response === null) {
      throw new Error('Server null error');
    }

    orderformService.handleOrderCreatedSuccess();
    emit('submit');
  } catch (error) {
    const { data } = error;
    orderformService.handleOrderCreationFailed(data?.error ?? '');
  } finally {
    isSubmitting.value = false;
  }
};

// Add computed for price that handles both market and limit orders
const displayPrice = computed(() => {
  if (activeType.value === marketType.type) {
    return marketOrderPrice.value;
  }
  return limitPriceValue.value;
});

// Update slider logic for percentages
const onSliderChange = (percentage: number) => {
  sliderPercentage.value = percentage;
  const amount = (Number(props.position.open_volume) * percentage) / 100;
  amountValue.value = amount.toString();
};

const valueFormatter = (value: number) => `${value.toFixed(2)}%`;

const { currencies, defaultFiatCurrency } = currencyService;

const showTooltips = ref({
  amountValue: false,
  totalValue: false,
});

const resetShowTooltips = () => {
  showTooltips.value = {
    amountValue: false,
    totalValue: false,
  };
};

// Update tooltipContent to handle initial state better
const tooltipContent = (inValue: string | null, inputName: 'amountValue' | 'totalValue') => {
  // Use either the passed value or the current ref value
  const value = Number(inValue ?? (inputName === 'amountValue' ? amountValue.value : totalValue.value));

  if (!currencies.value || !market.value) {
    return null;
  }

  const defaultFiatCurrencyPrecision = currencies.value[defaultFiatCurrency.value]?.precision ?? CONSTANTS.PRECISION.DEFAULT_FIAT;
  const currencyCode = market.value[inputName === 'amountValue' ? 'base' : 'quote'].code;
  const inputCurrencyInFiatValue = currencies.value[currencyCode]?.defaultFiatValue || 0;

  let fiatValue = value * inputCurrencyInFiatValue;
  fiatValue = trimNumberToPrecision(defaultFiatCurrencyPrecision)(fiatValue);

  return {
    content: `${fiatValue} ${defaultFiatCurrency.value}`,
    theme: 'tooltip-fiat',
    shown: showTooltips.value[inputName],
    placement: 'top-end',
    distance: 0.4,
  };
};

const onInputFocus = ($event: FocusEvent) => {
  if (!$event.target || ($event.target && !('name' in $event.target))) {
    return;
  }

  const { name } = $event.target;
  showTooltips.value[name as keyof typeof showTooltips.value] = true;
};

const onInputBlur = ($event: FocusEvent) => {
  resetShowTooltips();
};

// Update watch for amount/price changes with proper precision handling
watch(amountValue, (newAmount) => {
  if (newAmount && displayPrice.value) {
    const amount = trimNumberToPrecision(market.value?.amountPrecision ?? 8)(Number(newAmount));
    const price = Number(displayPrice.value);
    // Apply market precision to the total calculation
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

// Similar fix for other watchers that calculate total
watch(limitPriceValue, (newPrice) => {
  if (activeType.value === limitType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

watch(marketOrderPrice, (newPrice) => {
  if (activeType.value === marketType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

// Also watch for price type changes
watch(activeType, () => {
  // Recalculate total when switching between market/limit
  if (amountValue.value && displayPrice.value) {
    const amount = trimNumberToPrecision(market.value?.amountPrecision ?? 8)(Number(amountValue.value));
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * Number(displayPrice.value));
    totalValue.value = total.toString();
  }
});

// Watch limit price changes to update total only
watch(limitPriceValue, (newPrice) => {
  if (activeType.value === limitType.type && amountValue.value && newPrice) {
    // Keep the original amount, only update total
    const amount = Number(amountValue.value);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * Number(newPrice));
    totalValue.value = total.toString();
  }
});

// Add watch for marketOrderPrice changes
watch(marketOrderPrice, (newPrice) => {
  if (activeType.value === marketType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price);
    totalValue.value = total.toString();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_x_logo = _resolveComponent("x-logo")!
  const _component_ot_lozenge = _resolveComponent("ot-lozenge")!
  const _component_x_button = _resolveComponent("x-button")!
  const _component_x_input_number = _resolveComponent("x-input-number")!
  const _component_ot_input_error_wrapper = _resolveComponent("ot-input-error-wrapper")!
  const _component_x_slider = _resolveComponent("x-slider")!
  const _component_x_modal = _resolveComponent("x-modal")!

  return (_openBlock(), _createBlock(_component_x_modal, {
    class: "close-position",
    "can-close": true,
    wide: true,
    variant: __props.variant,
    title: "Close position",
    onClose: _cache[5] || (_cache[5] = ($event: any) => (emit('close')))
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_x_logo, {
                class: "position-header__icon",
                name: market.value?.base?.code,
                type: "crypto"
              }, null, 8, ["name"]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["position-header__direction", {
                      'position-header__direction--long': __props.position.direction === 'LONG',
                      'position-header__direction--short': __props.position.direction === 'SHORT',
                    }])
                  }, _toDisplayString(__props.position.direction === 'LONG' ? _ctx.$t('fundamentals.orders.side.long') : _ctx.$t('fundamentals.orders.side.short')), 3),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(market.value?.name), 1),
                  _createVNode(_component_ot_lozenge, {
                    text: _ctx.$t('fundamentals.perpetual.short'),
                    filled: false
                  }, null, 8, ["text"])
                ]),
                _createElementVNode("span", _hoisted_8, _toDisplayString(positionSize.value), 1)
              ])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_component_x_button, {
              class: _normalizeClass(["order-type-switcher__button order-type-switcher__button--left", { 'order-type-switcher__button--underlined': activeType.value === _unref(limitType).type, 'order-type-switcher__button--base': activeType.value !== _unref(limitType).type }]),
              size: "xs",
              rounded: "sm",
              variant: activeType.value === _unref(limitType).type ? 'underlined-primary' : 'base',
              text: "Limit",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (activeType.value = _unref(limitType).type))
            }, null, 8, ["class", "variant"]),
            _createVNode(_component_x_button, {
              class: _normalizeClass(["order-type-switcher__button order-type-switcher__button--right", { 'order-type-switcher__button--underlined': activeType.value === _unref(marketType).type, 'order-type-switcher__button--base': activeType.value !== _unref(marketType).type }]),
              size: "xs",
              rounded: "sm",
              variant: activeType.value === _unref(marketType).type ? 'underlined-primary' : 'base',
              text: "Market",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (activeType.value = _unref(marketType).type))
            }, null, 8, ["class", "variant"])
          ]),
          (activeType.value === _unref(limitType).type)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createVNode(_component_ot_input_error_wrapper, {
                  "no-message-placeholder": true,
                  error: _unref(v).limitPriceValue,
                  "error-messages": { required: 'Price is required' }
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_x_input_number, {
                      value: limitPriceValue.value,
                      "onUpdate:value": [
                        _cache[2] || (_cache[2] = ($event: any) => ((limitPriceValue).value = $event)),
                        onLimitPriceInput
                      ],
                      name: "limitPriceValue",
                      label: "Price",
                      state: _unref(v).limitPriceValue.$error ? 'error' : 'default',
                      variant: "dark",
                      compact: true,
                      precision: market.value?.marketPrecision,
                      currency: market.value?.quote?.code,
                      lpignore: true
                    }, null, 8, ["value", "state", "precision", "currency"])
                  ]),
                  _: 1
                }, 8, ["error"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_component_ot_input_error_wrapper, {
              error: _unref(v).amountValue,
              "error-messages": {
                required: 'Amount is required',
                minAmount: 'Amount must be greater than 0',
                maxAmount: 'Amount cannot exceed position size',
              }
            }, {
              default: _withCtx(() => [
                _createVNode(OrderSizeInput, {
                  "amount-value": amountValue.value,
                  "onUpdate:amountValue": [
                    _cache[3] || (_cache[3] = ($event: any) => ((amountValue).value = $event)),
                    onAmountInput
                  ],
                  "total-value": totalValue.value,
                  "onUpdate:totalValue": [
                    _cache[4] || (_cache[4] = ($event: any) => ((totalValue).value = $event)),
                    onTotalInput
                  ],
                  autofocus: false,
                  "amount-tooltip": tooltipContent(amountValue.value, 'amountValue'),
                  "total-tooltip": tooltipContent(totalValue.value, 'totalValue'),
                  market: market.value,
                  "input-state": _unref(v).amountValue.$error ? 'error' : 'default',
                  "order-side": orderSide.value,
                  onInputBlur: onInputBlur,
                  onInputFocus: onInputFocus
                }, null, 8, ["amount-value", "total-value", "amount-tooltip", "total-tooltip", "market", "input-state", "order-side"])
              ]),
              _: 1
            }, 8, ["error"])
          ]),
          _createVNode(_component_x_slider, {
            class: "close-position-form__slider",
            "model-value": sliderPercentage.value,
            "color-variant": 'primary',
            min: 0,
            max: 100,
            marks: [0, 25, 50, 75, 100],
            interval: 25,
            "step-style": { width: '6px', height: '6px', top: '-1px', left: '-1px' },
            duration: 0.2,
            "tooltip-formatter": valueFormatter,
            disabled: false,
            "drag-on-click": true,
            onChange: onSliderChange
          }, null, 8, ["model-value"]),
          _createVNode(_component_x_button, {
            class: "close-position-form__submit",
            disabled: _unref(v).$invalid || isSubmitting.value,
            loading: isSubmitting.value,
            text: "Close Position",
            variant: "primary",
            block: true,
            size: "md",
            onClick: onClosePosition
          }, null, 8, ["disabled", "loading"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["variant"]))
}
}

})